import {ChangeDetectionStrategy, Component, ElementRef, Inject, Injectable, OnDestroy, OnInit, signal, ViewChild} from '@angular/core';

import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AksjeeierbokId, AksjeeierEnkelModell, AksjeklasseModell, OppdaterBeholdningerSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {AksjeeierDatatjeneste} from "felles-ng2/tjenester/datalager/AksjeeierDatatjeneste";
import {AbstractControl, FormArray, FormControl, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatIconModule} from "@angular/material/icon";
import {MatToolbarModule} from "@angular/material/toolbar";
import {ErrorStateMatcher} from "@angular/material/core";
import {combineLatestWith, Observable} from "rxjs";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {VisAlltidErrorStateMatcher} from "felles-ng2/infrastruktur/material/VisAlltidErrorStateMatcher";
import {LukkAbonnementer} from "felles-ng2/infrastruktur/rxjs/LukkAbonnementer";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {MatButtonModule} from "@angular/material/button";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {Temporal} from "temporal-polyfill";
import {MatStepper, MatStepperModule} from "@angular/material/stepper";
import {AksjeeiereRedigerBeholdningKomponent, BeholdningerSkjemamodell} from "web-ng2/moduler/aksjeeierbok/komponenter/aksjeeiere-rediger-beholdning/aksjeeiere-rediger-beholdning";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {AksjeeiereBekreftBeholdningKomponent} from "web-ng2/moduler/aksjeeierbok/komponenter/aksjeeiere-bekreft-beholdning/aksjeeiere-bekreft-beholdning";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {AksjeklasseDatatjeneste} from "felles-ng2/tjenester/datalager/AksjeklasseDatatjeneste";
import {map} from "rxjs/operators";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {TranslateModule} from "@ngx-translate/core";
import {litenIkkeTomStreng} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {lesPlainDateSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import PlainDate = Temporal.PlainDate;
import {VisDersomIkkeScrolletTilBunnDirektiv} from "web-ng2/delt/direktiver/visDersomIkkeScrolletTilBunn/visDersomIkkeScrolletTilBunn";

const debug = initDebugger('AksjeeierRedigerDialogKomponent');


@Component({
    selector: 'aksjeeiere-rediger-beholdning-dialog',
    templateUrl: './aksjeeiere-rediger-beholdning-dialog.html',
    styleUrls: ['./aksjeeiere-rediger-beholdning-dialog.scss'],
    imports: [
        ReactiveFormsModule,
        FormaterPlainDatePipe,
        FormsModule,
        MatToolbarModule,
        MatIconModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        InlineMeldingKomponent,
        MatButtonModule,
        MatDatepickerModule,
        MatStepperModule,
        AksjeeiereRedigerBeholdningKomponent,
        TipsKomponent,
        AksjeeiereBekreftBeholdningKomponent,
        VisSpinnerDirektiv,
        TranslateModule,
        StandardValideringsfeilPipe,
        VisDersomIkkeScrolletTilBunnDirektiv
    ],
    providers: [
        { provide: ErrorStateMatcher, useClass: VisAlltidErrorStateMatcher }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AksjeeiereRedigerBeholdningDialogKomponent implements OnInit, OnDestroy {
    @ViewChild('endringsaarsakFelt') private endringsårsakFelt!: ElementRef<HTMLInputElement>;

    /** id til forespurt aksjeeierbok */
    private id: AksjeeierbokId
    /** register over åpnede subscriptions */
    private ab = new LukkAbonnementer();

    modell: BeholdningerSkjemamodell = new BeholdningerSkjemamodell([], [], this.ab, this.oversettelser); //begynner med tom modell - denne blir erstattet i init
    lagrer = signal(false);

    minimumInnfoeringsdato = Temporal.Now.plainDateISO();

    innfoeringsdato = new FormControl(<PlainDate|null> null, Validators.required);
    endringsaarsak = new FormControl('', litenIkkeTomStreng);
    //lager to skjemafeltsamlinger - en for hvert steg - slik at hver steg har et forhold om skjemaet for dette steget er gyldig
    //merk at av denne grunn omslutter vi ikke hele mat-stepper i en <form>
    skjemagruppeStegA = new FormArray([<AbstractControl>this.innfoeringsdato]);
    skjemagruppeStegB = new FormArray([this.skjemagruppeStegA, this.endringsaarsak]);

    static vis(id: AksjeeierbokId, dialog: MatDialog): MatDialogRef<AksjeeiereRedigerBeholdningDialogKomponent, void> {
        return dialog.open(AksjeeiereRedigerBeholdningDialogKomponent, {data: {id: id} as DialogParametre, disableClose: true});
    }

    constructor(public dialogRef: MatDialogRef<AksjeeiereRedigerBeholdningDialogKomponent>,
                private oversettelser: Oversettelser,
                private avhengigheter: AksjeeiereRedigerBeholdningDialogKomponentAvhengigheter,
                @Inject(MAT_DIALOG_DATA) parametre: DialogParametre) {
        debug("konstr", parametre)
        this.id = parametre.id;
        this.avhengigheter.finnAksjeeiereOgKlasserTilknyttet(this.id)
          .pipe(takeUntilDestroyed())
          .subscribe(eiererOgKlasser => this.initModell(eiererOgKlasser));
    }

    /** Gir fokus til årsak-feltet ved nav til bekreftelsessiden */
    stegskifteAnimasjonFerdig(steg: MatStepper) {
        //endringsårsakFelt vises ikke dersom det ikke finnes endringer
        if (erNil(this.endringsårsakFelt)) return;
        debug('stegskifteAnimasjonFerdig ' + this.endringsårsakFelt?.nativeElement, this.endringsårsakFelt)
        if (steg.selectedIndex == 1 && erIkkeNil(this.endringsårsakFelt.nativeElement))
            this.endringsårsakFelt.nativeElement.focus();
    }

    inneholderEndringer() {
        return this.modell.endredeBeholdninger().length > 0;
    }

    lagre() {
        const spesifikasjon = this.modell.somSpesifikasjon(this.innfoeringsdato.value!, this.endringsaarsak.value!);
        debug('lagrer', spesifikasjon)
        this.lagrer.set(true);
        this.avhengigheter.oppdaterBeholdninger(this.id, spesifikasjon)
          .then(() => this.dialogRef.close())
          .finally(() => this.lagrer.set(false));
    }

    avbryt() {
        this.dialogRef.close();
    }

    ngOnInit(): void {
        debug('$onInit', this.id);
           }

    ngOnDestroy(): void {
        this.ab.avsluttAlle();
    }

    //private hjelpemetoder

    private initModell(eiereOgKlasser: AksjeeiererOgKlasser) {
        debug('initModell', eiereOgKlasser);
        this.modell = new BeholdningerSkjemamodell(eiereOgKlasser.aksjeeiere, eiereOgKlasser.aksjeklasser, this.ab, this.oversettelser);

        //initModell kan kalles flere ganger - må derfor resette skjemagruppeStegA for at ikke gamle aksjenummerSkjemakontroller instanser skal akkumulere
        this.skjemagruppeStegA.clear();
        this.skjemagruppeStegA.push(this.innfoeringsdato);
        this.skjemagruppeStegA.push(this.modell.aksjenummerSkjemakontroller);

        //sørger for at foreslått innføringsdato ikke er mindre enn seneste eksisterende innføringsdato, de kan være like
        const innføringsdatoer = eiereOgKlasser.aksjeeiere
          .filter(aksjeeiere => erIkkeNil(aksjeeiere.naavaerendeBeholdning))
          .map(aksjeeiere => lesPlainDateSerialisert(aksjeeiere.naavaerendeBeholdning!.gyldigFraTil.start))
          .sort(PlainDate.compare)
          .reverse();
        this.minimumInnfoeringsdato = innføringsdatoer.length > 0 ? innføringsdatoer[0] : new PlainDate(1900, 1, 1);

    }
}


export interface DialogParametre {
    id: AksjeeierbokId
}

export interface AksjeeiererOgKlasser {
    aksjeeiere: AksjeeierEnkelModell[];
    aksjeklasser: AksjeklasseModell[];
}

@Injectable()
export class AksjeeiereRedigerBeholdningDialogKomponentAvhengigheter {

    constructor(private aksjeeierDatatjeneste: AksjeeierDatatjeneste,
                private aksjeklasseDatatjeneste: AksjeklasseDatatjeneste) {
    }

    finnAksjeeiereOgKlasserTilknyttet(aksjeeierbokId: AksjeeierbokId): Observable<AksjeeiererOgKlasser> {
        return this.aksjeeierDatatjeneste.finnAksjeeiereTilknyttet(aksjeeierbokId).pipe(
          combineLatestWith(this.aksjeklasseDatatjeneste.finnAksjeklasserTilknyttet(aksjeeierbokId)),
          map(([aksjeeiere, aksjeklasser]) => ({aksjeeiere, aksjeklasser})));
    }

    oppdaterBeholdninger(id: AksjeeierbokId, spesifikasjon: OppdaterBeholdningerSpesifikasjon): Promise<any> {
        return this.aksjeeierDatatjeneste.oppdaterBeholdninger(id, spesifikasjon);
    }
}
